
import type { PropType } from 'vue'
import type {
  TaggingActionDataItem,
  TaggingData,
  TaggingDataItem,
  TaggingViewDataItem,
} from '../../models'
import type { FeatureConfigProvider } from '../../providers/interfaces/feature-config.provider'
import type { SegmentedBarDataItem } from '../segmented-bar.component.vue'
import { EngagementActionsTypeEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-actions-type.enum'
import { EngagementTypeComponentEnum } from '@empathybroker/tsclient-stats/dist/enums/engagement/engagement-type-component.enum'
import { defineComponent } from 'vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { ColorEnum, TaggingTypeEnum } from '../../enums'
import GaugeSemicircleChart from '../gauge-semicircle-chart.component.vue'
import { UiAlert, UiProgressLinear } from '../keen-ui'
import SegmentedBar from '../segmented-bar.component.vue'
import { EpbButton } from '../shared/epb-button'
import { EpbText } from '../shared/epb-text'
import { EpbToggle } from '../shared/epb-toggle'
import TransitionSize from '../transition-size.component.vue'

export default defineComponent({
  name: 'TaggingBanner',
  components: {
    EpbButton,
    EpbText,
    EpbToggle,
    GaugeSemicircleChart,
    SegmentedBar,
    TransitionSize,
    UiAlert,
    UiProgressLinear,
  },
  props: {
    data: {
      type: Object as PropType<TaggingData>,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    messages: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      baseClass: 'epb-tagging-banner',
      mode: TaggingTypeEnum.VIEW,
      selectedBarDataItem: undefined,
      hoveredBarDataItem: undefined,
      arcActions: [
        EngagementActionsTypeEnum.CREATE,
        EngagementActionsTypeEnum.EDIT,
        EngagementActionsTypeEnum.DELETE,
        EngagementActionsTypeEnum.SEARCH,
        EngagementTypeComponentEnum.DOWNLOAD,
      ],
    }
  },
  computed: {
    modeOptions(): { id: string; content: string; tooltip: string }[] {
      return Object.values(TaggingTypeEnum).map((id: TaggingTypeEnum) => ({
        id,
        content: {
          [TaggingTypeEnum.VIEW]: 'showsolid',
          [TaggingTypeEnum.ACTION]: 'actions',
        }[id],
        tooltip: this.messages.modes[id].description,
      }))
    },
    counterValue(): number {
      if (!!this.hoveredBarDataItem || !!this.selectedBarDataItem) {
        return this.hoveredBarDataItem?.value || this.selectedBarDataItem?.value
      }
      if (this.mode === TaggingTypeEnum.ACTION) {
        return this.data.action.reduce((counter: number, dataItem: TaggingActionDataItem) => {
          const typesCount = Object.values(dataItem.types).reduce(
            (result: number, typeCount: number) => {
              return result + typeCount
            },
            0,
          )
          return counter + typesCount
        }, 0)
      }
      return this.data.view.reduce((counter: number, dataItem: TaggingViewDataItem) => {
        return counter + dataItem.count
      }, 0)
    },
    barData(): SegmentedBarDataItem[] {
      const modeData = this.mode === TaggingTypeEnum.ACTION ? this.data.action : this.data.view
      return modeData.map((dataItem: TaggingDataItem) => {
        return this.getBarDataItem(dataItem)
      })
    },
    arcsVisible(): boolean {
      return this.mode === TaggingTypeEnum.ACTION
    },
    arcColor(): ColorEnum {
      return this.hoveredBarDataItem?.color || this.selectedBarDataItem?.color || ColorEnum.BLACK
    },
  },
  methods: {
    setSelectedBarDataItem(value: SegmentedBarDataItem): void {
      if (!value || !!value.children?.length) {
        this.setHoveredBarDataItem(undefined)
        this.selectedBarDataItem = value
      }
    },
    setHoveredBarDataItem(value: SegmentedBarDataItem): void {
      if (!value || this.selectedBarDataItem !== value) {
        this.hoveredBarDataItem = value
      }
    },
    onModeChange(mode: TaggingTypeEnum): void {
      this.clear()
      this.mode = mode
    },
    getArcValue(action: EngagementActionsTypeEnum): number {
      let actionCount
      const hoveredOrSelectedId = this.hoveredBarDataItem?.id || this.selectedBarDataItem?.id
      if (hoveredOrSelectedId) {
        actionCount = this.getDataItemById(hoveredOrSelectedId, this.data.action).types[action]
      } else {
        actionCount = this.data.action.reduce(
          (counter: number, dataItem: TaggingActionDataItem) => {
            const itemCount: number = dataItem.types[action] ?? 0
            return counter + itemCount
          },
          0,
        )
      }
      let arcValue = (actionCount / this.counterValue) * 100
      if (isNaN(arcValue)) {
        arcValue = 0
      }
      return Math.round(arcValue * 1e2) / 1e2
    },
    getDataItemById(
      id: TaggingActionDataItem['nodeId'],
      arr: Array<TaggingActionDataItem>,
    ): TaggingActionDataItem {
      let resultDataItem = null

      arr.forEach((dataItem: TaggingActionDataItem) => {
        if (dataItem.nodeId === id) {
          resultDataItem = dataItem
        } else {
          const childrenResultDataItem = this.getDataItemById(id, dataItem.children)
          if (childrenResultDataItem) {
            resultDataItem = childrenResultDataItem
          }
        }
      })

      return resultDataItem
    },
    getBarDataItem(dataItem: TaggingDataItem): SegmentedBarDataItem {
      const getFeatureConfigProvider = CONTAINER.get<FeatureConfigProvider>(
        TYPES.FeatureConfigProvider,
      )

      const { name, color } = getFeatureConfigProvider(dataItem.nodeId, {
        domain: dataItem.domain,
      })

      const barDataItem: SegmentedBarDataItem = {
        id: dataItem.nodeId,
        label: name,
        value: 0,
        color,
      }
      if (this.mode === TaggingTypeEnum.VIEW) {
        barDataItem.value = (dataItem as TaggingViewDataItem).count
      } else {
        barDataItem.value = Object.values((dataItem as TaggingActionDataItem).types).reduce(
          (counter: number, action: number) => {
            return counter + action
          },
          0,
        )
      }
      if (dataItem.children.length) {
        barDataItem.children = dataItem.children
          .map((childDataItem: TaggingDataItem) => {
            return this.getBarDataItem(childDataItem)
          })
          .sort(
            (childDataItem1: SegmentedBarDataItem, childDataItem2: SegmentedBarDataItem) =>
              childDataItem2.value - childDataItem1.value,
          )
      }
      return barDataItem
    },
    clear(): void {
      this.setSelectedBarDataItem(undefined)
      this.setHoveredBarDataItem(undefined)
    },
  },
})
